import { api, urls } from '@/api'
import errors from '@/apiHandleErrors'
import local from '@/localStorage'
import { checkResponse, sanitizeParams } from '@/store/helpers'
import * as types from '@/store/mutation-types'
import Vue from 'vue'
import Vuex from 'vuex'

// modules
import buy from '@/store/modules/buy'
import category from '@/store/modules/category'
import filters from '@/store/modules/filters'
import publication from '@/store/modules/publication'
import purchasesList from '@/store/modules/purchases-list'
import sale from '@/store/modules/sale'
import salesList from '@/store/modules/sales-list'
import sell from '@/store/modules/sell'
import user from '@/store/modules/user'
import favorites from './modules/favorites'
import profile from './modules/profile'

Vue.use(Vuex)

/**
 * Sets debug observers
 */
const debug = process.env.NODE_ENV !== 'production'

const DESKTOP_FIRST_REQUEST = 24
const DESKTOP_REQUEST = 24
const MOBILE_FIRST_REQUEST = 12
const MOBILE_REQUEST = 12

const state = {
  // Public Frontend Version to be compared with the API responses
  frontendVersion: '2.65',
  // Latest migrated publication ID: cannot republish migrated publications
  lastMigratedPub: 20763,
  // If true, client needs to refresh browser
  frontendOutOfDate: false,
  // Control flag to limit check version message
  versionChecked: false,
  // If true, maintenance is undergoing on the site
  maintenance: false,
  // Location parameters
  location: {
    lat: null,
    lon: null
  },
  currentCity: null,
  // Cities and distances from API
  locations: {
    near_cities: [],
    popular_cities: [],
    distances: [10, 25, 50, 100, 250] // this can be gettedfrom api with store.dispatch('fetchSearchLocations')
  },
  // Controls for paginated API response
  adsPage: 1,
  // Search parameters for user manipulation
  search: {
    distance: -1,
    // user_agent: null,
    page: 1,
    per_page: 36
  },
  // search
  q: '',
  // search purchases
  purchases_q: '',
  // search sales
  sales_q: '',
  // Categories list from API
  categories: [],
  // Publications from API
  pubs: [],
  // advertisements from API
  ads: [],
  temps: {},
  isFetching: false,
  refreshHome: false,
  paymentGetawayList:{
    mercadopago: 'MERCADOPAGO_MKT'
  },
  shippingTypeList: [
    {value: 'INCLUDED_IN_PRICE', text: 'Incluido en el precio'},
    {value: 'TO_AGREE', text: 'A convenir con el comprador'},
    {value: 'STORE_PICKUP', text: 'Retira en el local'}
  ],
  statusPayments: {
    PAID: 'Aprobado',
    UNPAID: 'Impago',
    REJECTED: 'Rechazado',
    PAYMENT_REQUIRED: 'Rechazado',
    APPROVED: 'Aprobado',
    REFUNDED: 'Devuelto'
  }
}

const getters = {
  location: (state) => {
    let output = {}
    state.location.lat ? output.lat = state.location.lat : null
    state.location.lon ? output.lon = state.location.lon : null
    state.search.distance || state.search.distance === 0 ? output.distance = state.search.distance : null
    state.currentCity ? output.city = state.currentCity : null
    return output
  }
}

const actions = {

  pushToDataLayer: ({}, eventData) => {
    window.dataLayer.push(eventData)
  },

  fetchCategories: ({commit}) => {
    const cats = local.getCategoriesTree()

    return cats
      ? commit(types.SET_CATEGORIES, cats)
      : api.get(urls.categories)
        .then(({data}) => {
          if (data.data) {
            commit(types.SET_CATEGORIES, data.data)
            local.setCategoriesTree(data.data)
          }
        })
        .catch(errors.fetchCategories)
  },

  /**
   * Gets and store user_agent from browser.
   *
   * @uses commit to set user agent.
   */
  getUserAgent: ({commit}) => {
    const agent = window.navigator.userAgent
    commit(types.SET_USER_AGENT, agent)
  },

  /**
   * Fetches publications from API and populates the state.
   *
   * @uses maskParams filter parameters to API request.
   * @uses getParams clone parameters from state.
   * @uses commit to append fetched pubs and change the page for next search.
   *
   * @return {boolean} whether has more pubs left
   */
  fetchPubs: ({commit}, isMobile) => {
    const location = local.getLocation() ? local.getLocation() : state.location
    const params = sanitizeParams('pubs')({...state.search, ...location})

    if (isMobile) {
      params.per_page = state.pubs.length > 0 ? MOBILE_REQUEST : MOBILE_FIRST_REQUEST
    } else {
      params.per_page = state.pubs.length > 0 ? DESKTOP_REQUEST : DESKTOP_FIRST_REQUEST
    }

    return api.get(`${urls.publications}/home`, {params})
      .then(checkResponse)
      .then(({data, next}) => {
        commit(types.APPEND_PUBS, data)
        if (next) commit(types.NEXT_PAGE, 'pubs')

        return next
      })
      .catch(errors.fetchPubs)
  },

  fetchAds: ({commit}) => {
    let location = local.getLocation() ? local.getLocation() : state.location
    location = {
      ...location,
      distance: state.search.distance
    }
    const preParams = {
      ...location,
      user_agent: state.search.user_agent,
      per_page: 50
    }
    const params = sanitizeParams('ads')(preParams)

    return api.get(`${urls.ads}/home`, {params})
      .then(checkResponse)
      .then(({data, next}) => {
        commit(types.APPEND_ADS, data)
        if (next) commit(types.NEXT_PAGE, 'ads')

        return next
      })
      .catch(errors.fetchAds)
  },

  resetSearch: ({commit}) => {
    commit(types.SET_PUBS, [])
    commit(types.SET_ADS, [])
    commit(types.RESET_PAGE, 'pubs')
    commit(types.RESET_PAGE, 'ads')
  },

  getLocation: ({commit}) => api.get(urls.me)
    .then(({data}) => {
      if (data.data) {
        const location = data.data.preferred_city

        // sets lat & lon
        if (location) {
          const lat = parseFloat(location.latitude)
          const lon = parseFloat(location.longitude)
          let city = ''
          commit(types.SET_LOCATION, {lat, lon, city})

          return true
        }
      }
      return false
    })
    .catch(errors.getLocation),

  setLocation: ({commit, dispatch, getters}, {coords, sync}) => new Promise((resolve, reject) => {
    const lat = parseFloat(coords.lat).toFixed(5)
    const lon = parseFloat(coords.lon).toFixed(5)
    const city = coords.city || 'cerca tuyo'

    local.setLocation({lat, lon, city})
    commit(types.SET_LOCATION, {lat, lon, city})
    if (sync && state.user.isAuth) {
      const search = {
        latitude: lat.toString(),
        longitude: lon.toString(),
        city: city,
        search_distance: -1
      }
      const userInfo = {
        person: state.user.person,
        preferred_location: search
      }
      dispatch('user/updateUserInfo', userInfo).then(() => resolve(true))
    } else {
      resolve(true)
    }
  }),

  setSearchQuery: ({commit}, q) => new Promise((resolve, reject) => {
    commit(types.SET_SEARCH_QUERY, q)
    resolve(true)
  }),

  // TODO: remove this
  setSearchPurchasesQuery: ({commit}, q) => new Promise((resolve, reject) => {
    commit(types.SET_SEARCH_PURCHASES_QUERY, q)
    resolve(true)
  }),

  // TODO: remove this
  setSearchSalesQuery: ({commit}, q) => new Promise((resolve, reject) => {
    commit(types.SET_SEARCH_SALES_QUERY, q)
    resolve(true)
  }),

  fetchSearchLocations: ({commit}) => {
    if (state.locations.near_cities.length > 0) return Promise.resolve()

    return api.get(urls.locations)
      .then(({data}) => { commit(types.SET_SEARCH_LOCATIONS, data.data) })
      .catch(errors.fetchSearchLocations)
  },

  setDistance: ({commit}, dist) => {
    commit(types.SET_DISTANCE, dist)
  },

  setIsFetching: ({commit}, value) => {
    commit(types.SET_IS_FETCHING, value)
  },

  setRefreshHome: ({commit}, value) => {
    commit(types.SET_REFRESH_HOME, value)
  },

  setFrontendOutOfDate: ({commit}, value) => {
    commit(types.SET_FRONTEND_OUT_OF_DATE, value)
  },

  setVersionChecked: ({commit}, value) => {
    commit(types.SET_VERSION_CHECKED, value)
  },

  setMaintenance: ({commit}, value) => {
    commit(types.SET_MAINTENANCE, value)
  },

  setTempLocations: ({commit}, locations) => {
    commit(types.SET_TEMP_LOCATIONS, locations)
  },

  showSecurityNotice: () => {
    return import('@/services/security')
      .then(({ default: securityService }) => {
        return securityService.showNotice()
      })
  }
}

const mutations = {
  /**
   * Sets whole categories array.
   *
   * @param {array} categories
   */
  [types.SET_CATEGORIES] (state, categories) {
    state.categories = categories
  },

  /**
   * Sets lat & lon.
   *
   * @param {object[lat, lon]} coords
   */
  [types.SET_LOCATION] (state, coords) {
    state.location = {
      lat: parseFloat(coords.lat),
      lon: parseFloat(coords.lon)
    }
    state.currentCity = coords.city
  },

  [types.SET_PUBS] (state, pubs) {
    state.pubs = pubs
  },

  [types.SET_ADS] (state, ads) {
    state.ads = ads
  },

  /**
   * Increment in one a page control index.
   *
   * @param {string} page name of item should be incremented.
   */
  [types.NEXT_PAGE] (state, page) {
    if (page === 'pubs') state.search.page = 1 + state.search.page
    if (page === 'ads') state.adsPage = 1 + state.adsPage
  },

  [types.RESET_PAGE] (state, page) {
    if (page === 'pubs') state.search.page = 1
    if (page === 'ads') state.adsPage = 1
  },

  /**
   * Appends publications with previus.
   *
   * @param {array} pubs publications to be added.
   */
  [types.APPEND_PUBS] (state, pubs) {
    state.pubs = [...state.pubs, ...pubs]
  },

  /**
   * Appends advertisements with previus.
   *
   * @param {array} ads advertisements to be added.
   */
  [types.APPEND_ADS] (state, ads) {
    state.ads = [...state.ads, ...ads]
  },

  /**
   * Sets user agent.
   *
   * @param {string} agent
   */
  [types.SET_USER_AGENT] (state, agent) {
    state.search.user_agent = agent
  },

  [types.SET_DISTANCE] (state, dist) {
    state.search.distance = dist
  },

  [types.SET_SEARCH_QUERY] (state, q) {
    state.q = q
  },

  [types.SET_SEARCH_PURCHASES_QUERY] (state, q) {
    state.purchases_q = q
  },

  // TODO: remove this
  [types.SET_SEARCH_SALES_QUERY] (state, q) {
    state.sales_q = q
  },

  [types.SET_SEARCH_LOCATIONS] (state, data) {
    state.locations.near_cities = data.near_cities
    state.locations.popular_cities = data.popular_cities
    state.locations.distances = data.distances[0]
  },

  [types.SET_IS_FETCHING] (state, value) {
    state.isFetching = value
  },

  [types.SET_REFRESH_HOME] (state, value) {
    state.refreshHome = value
  },

  [types.SET_FRONTEND_OUT_OF_DATE] (state, value) {
    state.frontendOutOfDate = value
  },

  [types.SET_VERSION_CHECKED] (state, value) {
    state.versionChecked = value
  },

  [types.SET_MAINTENANCE] (state, value) {
    state.maintenance = value
  },

  [types.SET_TEMP_LOCATIONS] (state, locations) {
    state.temps.newLocations = locations
  }
}

export default new Vuex.Store({
  state,
  getters,
  actions,
  strict: debug,
  mutations,
  modules: {
    buy,
    category,
    favorites,
    filters,
    profile,
    publication,
    sale,
    salesList,
    purchasesList,
    sell,
    user
  }
})



// WEBPACK FOOTER //
// ./src/store/store.js